import { Box, Timeline, Label, Octicon, ButtonGroup, Button } from '@primer/react';
import { GitCommitIcon } from '@primer/octicons-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ArrowSquareOut, ArrowCounterClockwise, Check, Spinner } from 'phosphor-react';
import { GlobalModal } from '@/features/misc/components/GlobalModal';
import { useState } from 'react';
import { useNotificationStore } from '@/stores/notifications';
import { WorkflowCheckpoint } from '../api/getWorkflowCheckpoints';
import useWorkflowOperations from '@/features/app/hooks/useWorkflowOperations';
import CustomBlankslate from '@/components/Elements/CustomBlankSlate/CustomBlankSlate';
import { useRvertToCheckpoint } from '../api/revertToCheckpoint.';
import { getDownloadWorkflow } from '../api/getDownloadWorkflow';

dayjs.extend(relativeTime);

interface CommitsTimelineProps {
  checkpoints: any;
  isLoading: boolean;
  searchTerm: string;
  workflowName: string;
  workflow_id: string;
  onSuccessfulRevert?: () => void;
  projectId?: any;
  selectedApiFileId?: string;
  selectedNormalFileId?: string;
}

const CommitsTimeline: React.FC<CommitsTimelineProps> = ({
  checkpoints,
  isLoading,
  searchTerm,
  workflowName,
  workflow_id,
  onSuccessfulRevert,
  projectId,
  selectedApiFileId,
  selectedNormalFileId,
}) => {
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<WorkflowCheckpoint | null>(null);
  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [revertSuccess, setRevertSuccess] = useState(false);
  const [newVersionNumber, setNewVersionNumber] = useState<number | null>(null);

  const { addNotification } = useNotificationStore();
  const { mutateAsync: revertToCheckpoint } = useRvertToCheckpoint(projectId);

  const { getSelectedWorkflowId, loadWorkflow } = useWorkflowOperations();

  const fileName = workflowName.split('/').pop();

  // Handle the new nested structure of checkpoints
  const checkpointsList = checkpoints?.checkpoints || [];

  const filteredCheckpoints = checkpointsList
    ?.filter((checkpoint: WorkflowCheckpoint) =>
      checkpoint?.commit_message?.toLowerCase()?.includes(searchTerm.toLowerCase())
    )
    ?.sort((a: WorkflowCheckpoint, b: WorkflowCheckpoint) => (b.version || 0) - (a.version || 0));

  // Get the current version (highest version number)
  const currentVersion =
    filteredCheckpoints?.length > 0
      ? Math.max(...filteredCheckpoints.map((cp: WorkflowCheckpoint) => cp.version || 0))
      : 0;

  // Helper function to clean commit message (remove version prefix)
  const getCleanCommitMessage = (message: string) => {
    // Check if message starts with version pattern like "v17: "
    if (message && /^v\d+\s*:\s*/.test(message)) {
      return message.replace(/^v\d+\s*:\s*/, '');
    }
    return message;
  };

  // Helper function to check if commit is a revert operation
  const isRevertOperation = (message: string) => {
    return message && (message.includes('Switched to') || message?.includes('Rename workflow'));
  };

  const isSaveChangesOperation = (message: string) => {
    return message && message.includes('Switched to');
  };

  const handleConfirmRevert = async () => {
    if (!selectedCheckpoint || revertSuccess) return;
    setIsReverting(true);

    try {
      // Pass the complete checkpoint data to the revertWorkflowVersion function
      await revertToCheckpoint({
        project_id: projectId,
        workflow_id,
        destination_version: selectedCheckpoint.version,
      });

      setRevertSuccess(true);
      setNewVersionNumber(selectedCheckpoint.version);

      addNotification({
        type: 'success',
        title: 'File Successfully Reverted',
      });

      if (getSelectedWorkflowId(projectId) === workflow_id) {
        if (!selectedNormalFileId) {
          return console.error('No normal file ID selected for revert operation.');
        }
        const response = await getDownloadWorkflow(projectId, selectedNormalFileId);
        if (!response || !response.data || !response.data.download_url) {
          console.error('Failed to get download URL for workflow');
        }
        const { download_url } = response.data;

        // 2) Download the JSON from the returned URL
        const workflowRes = await fetch(download_url);
        const workflowJson = await workflowRes.json();

        loadWorkflow(workflowJson); // Call the workflow loader
      }

      // Close modal after a delay to show success state
      setTimeout(() => {
        setIsRevertModalOpen(false);
        setRevertSuccess(false);
        setNewVersionNumber(null);

        // Call the onSuccessfulRevert callback if provided
        if (onSuccessfulRevert) {
          onSuccessfulRevert();
          setIsReverting(false);
          setRevertSuccess(false);
        }
      }, 2000);
    } catch (error) {
      console.error('Error reverting file version:', error);
      addNotification({
        type: 'error',
        title: 'Revert Failed',
        message: `Failed to revert ${fileName}. Please try again.`,
      });
      setIsReverting(false);
      setRevertSuccess(false);
    }
  };

  if (isLoading) {
    return (
      <Box className="flex justify-center my-10 items-center gap-2">
        <Spinner className="animate-spin" />
        Please wait, loading commits...
      </Box>
    );
  }

  // Get version and clean message for the selected checkpoint
  const selectedVersion = selectedCheckpoint
    ? {
        version: selectedCheckpoint?.version,
        cleanMessage: getCleanCommitMessage(selectedCheckpoint?.commit_message),
      }
    : { version: null, cleanMessage: '' };

  return (
    <Box className="overflow-y-auto max-h-[405px] px-3">
      <GlobalModal
        isOpen={isRevertModalOpen}
        isClose={() => {
          if (!isReverting) {
            setIsRevertModalOpen(false);
          }
        }}
        title="Revert Version"
        primaryButtonClick={handleConfirmRevert}
        buttonText={revertSuccess ? 'Done' : 'Confirm'}
        isPrimaryButtonLoading={isReverting}
        disableModalClose={isReverting}
      >
        {revertSuccess ? (
          <Box className="flex flex-col items-center py-4 gap-3">
            <div className="bg-green-800 rounded-full p-2">
              <Check size={32} weight="bold" className="text-green-400" />
            </div>
            <p className="text-lg font-medium">File Successfully Reverted</p>
            <p className="text-sm text-gray-400 text-center">
              A new checkpoint (v{newVersionNumber}) has been created with content from v
              {selectedVersion.version}.
            </p>
          </Box>
        ) : (
          <Box className="flex flex-col gap-3">
            <p className="text-base">Are you sure you want to revert to this version?</p>
            <p className="text-sm text-gray-400">
              This will create a new version (v{currentVersion + 1}) with the content from v
              {selectedCheckpoint?.version}. A new commit will be created with the message "v
              {currentVersion + 1}: Revert back to v{selectedCheckpoint?.version} from v
              {currentVersion}".
            </p>
            {selectedCheckpoint && (
              <Box className="bg-gray-800 p-4 flex gap-3 rounded-md mt-2">
                <Box className="flex items-center gap-2 mb-2">
                  {selectedVersion.version !== null ? (
                    <Label variant="accent" sx={{ fontSize: '14px' }}>
                      v{selectedVersion.version}
                    </Label>
                  ) : (
                    <Label variant="accent">Selected Version</Label>
                  )}
                </Box>
                <p className="text-sm font-medium">{selectedVersion.cleanMessage}</p>
              </Box>
            )}
          </Box>
        )}
      </GlobalModal>

      {!filteredCheckpoints || filteredCheckpoints.length === 0 ? (
        <Box className="flex justify-center items-center p-4">
          <CustomBlankslate
            icon={<GitCommitIcon size={30} />}
            heading="No Changes Found"
            description="There are no commits."
          />
        </Box>
      ) : (
        <Timeline>
          {filteredCheckpoints.map((checkpoint: WorkflowCheckpoint) => {
            const cleanMessage = getCleanCommitMessage(checkpoint.commit_message);
            const isRevert = isRevertOperation(checkpoint.commit_message);
            const isSaveChanges = isSaveChangesOperation(checkpoint.commit_message);
            const isLatest = checkpoint.version === currentVersion;

            return (
              <Timeline.Item key={checkpoint?.version}>
                <Timeline.Badge>
                  <Octicon icon={GitCommitIcon} />
                </Timeline.Badge>
                <Timeline.Body className="flex justify-between">
                  <Box className="flex flex-col justify-start items-start gap-2">
                    <Box className="flex items-center gap-2">
                      <Label variant="accent" sx={{ mr: 1 }}>
                        v{checkpoint.version}
                      </Label>
                      <h3 className="text-base font-semibold">{cleanMessage}</h3>
                    </Box>
                    <Box className="flex gap-1 items-center text-sm font-normal">
                      <p>Created at {dayjs(checkpoint.created_at).format('YYYY-MM-DD')}</p>
                      <p>{checkpoint?.commit_author && `by ${checkpoint?.commit_author}`}</p>
                    </Box>
                  </Box>
                  <Box className="flex items-center gap-2">
                    <ButtonGroup>
                      {!isRevert && !isLatest && (
                        <Button
                          aria-label="Revert to this version"
                          leadingVisual={ArrowCounterClockwise}
                          onClick={() => {
                            setSelectedCheckpoint(checkpoint);
                            setIsRevertModalOpen(true);
                          }}
                          size="small"
                        >
                          Revert
                        </Button>
                      )}
                      {/* {!isSaveChanges && (
                        <Button
                          aria-label="See changes"
                          leadingVisual={ArrowSquareOut}
                          onClick={() => {
                            // Construct GitHub split view URL using api_workflow_sha
                            const splitViewUrl = checkpoint?.api_workflow_sha
                              ? `https://github.com/${username}/${repo_name}/commit/${checkpoint.api_workflow_sha}?diff=split`
                              : `https://github.com/${username}/${repo_name}/commit/${checkpoint.sha}?diff=split`;
                            window.open(splitViewUrl, '_blank');
                          }}
                          size="small"
                        >
                          See changes
                        </Button>
                      )} */}
                    </ButtonGroup>
                  </Box>
                </Timeline.Body>
              </Timeline.Item>
            );
          })}
        </Timeline>
      )}
    </Box>
  );
};

export default CommitsTimeline;
